import { forwardRef, ComponentPropsWithoutRef, ImgHTMLAttributes } from 'react'
import { useDialogState } from '@/hooks'
import { PortraitSide } from '@/store/types'

/**
 * Portrait Context - 用于在子组件间共享状态
 */
import { createContext, useContext } from 'react'

interface PortraitContextValue {
  side: PortraitSide
  characterKey: string | undefined
  characterImage: string | undefined
  emotionImage: string | undefined
  isHighlighted: boolean
}

const PortraitContext = createContext<PortraitContextValue | null>(null)

const usePortraitContext = () => {
  const context = useContext(PortraitContext)
  if (!context) {
    throw new Error('Portrait components must be used within Portrait.Root')
  }
  return context
}

/**
 * Portrait Root 组件
 * Headless 立绘组件根元素
 */
interface PortraitRootProps extends ComponentPropsWithoutRef<'div'> {
  side: PortraitSide
  characterImages?: Record<string, string>
  emotionImages?: Record<string, Record<string, string>>
}

const PortraitRoot = forwardRef<HTMLDivElement, PortraitRootProps>(
  ({ side, characterImages = {}, emotionImages = {}, children, ...props }, ref) => {
    const state = useDialogState()

    if (!state.isVisible || !state.currentDialogue) {
      return null
    }

    const { currentDialogue } = state
    const characterKey =
      side === 'left' ? currentDialogue.leftPortrait : currentDialogue.rightPortrait
    const emotion =
      side === 'left' ? currentDialogue.leftEmotion : currentDialogue.rightEmotion
    const isHighlighted = currentDialogue.highlight === side

    if (!characterKey) {
      return null
    }

    const characterImage = characterImages[characterKey]
    const emotionImage = emotion ? emotionImages[characterKey]?.[emotion] : undefined

    const contextValue: PortraitContextValue = {
      side,
      characterKey,
      characterImage,
      emotionImage,
      isHighlighted,
    }

    return (
      <PortraitContext.Provider value={contextValue}>
        <div
          ref={ref}
          data-side={side}
          data-highlighted={isHighlighted}
          data-character={characterKey}
          {...props}
        >
          {children}
        </div>
      </PortraitContext.Provider>
    )
  }
)

PortraitRoot.displayName = 'Portrait.Root'

/**
 * Portrait Image 组件
 * 显示角色基础图片
 */
interface PortraitImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  fallback?: React.ReactNode
}

const PortraitImage = forwardRef<HTMLImageElement, PortraitImageProps>(
  ({ fallback, ...props }, ref) => {
    const { characterImage, side } = usePortraitContext()

    if (!characterImage) {
      return fallback ? <>{fallback}</> : null
    }

    return <img ref={ref} src={characterImage} alt={`${side} character`} {...props} />
  }
)

PortraitImage.displayName = 'Portrait.Image'

/**
 * Portrait Emotion 组件
 * 显示角色表情图片
 */
interface PortraitEmotionProps extends ImgHTMLAttributes<HTMLImageElement> {}

const PortraitEmotion = forwardRef<HTMLImageElement, PortraitEmotionProps>(
  (props, ref) => {
    const { emotionImage, side } = usePortraitContext()

    if (!emotionImage) {
      return null
    }

    return (
      <img ref={ref} src={emotionImage} alt={`${side} character emotion`} {...props} />
    )
  }
)

PortraitEmotion.displayName = 'Portrait.Emotion'

/**
 * Portrait Placeholder 组件
 * 当没有图片时显示的占位符
 */
interface PortraitPlaceholderProps extends ComponentPropsWithoutRef<'div'> {}

const PortraitPlaceholder = forwardRef<HTMLDivElement, PortraitPlaceholderProps>(
  ({ children, ...props }, ref) => {
    const { characterImage } = usePortraitContext()

    if (characterImage) {
      return null
    }

    return (
      <div ref={ref} {...props}>
        {children}
      </div>
    )
  }
)

PortraitPlaceholder.displayName = 'Portrait.Placeholder'

export const Portrait = {
  Root: PortraitRoot,
  Image: PortraitImage,
  Emotion: PortraitEmotion,
  Placeholder: PortraitPlaceholder,
}
